import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { gradientText } from '../styles/mixins';

const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const TriggerButton = styled.button`
  background: none;
  border: none;
  color: white;
  margin: 0;
  padding: 0;
  font-size: ${props => props.size === 'large' ? '1.8rem' : '1.2rem'};
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  ${gradientText('#4CAF50', '#2196F3', '#4CAF50')}
  position: relative;
  
  &:hover {
    transform: scale(1.02);
  }
  
  &:focus {
    outline: none;
  }
  
  ${props => props.clickable && `
    &:after {
      content: '▼';
      margin-left: 8px;
      font-size: 0.8em;
      opacity: 0.6;
      color: white;
      -webkit-text-fill-color: rgba(255, 255, 255, 0.6);
    }
  `}
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px 0;
  min-width: 200px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
  z-index: 10000;
  opacity: ${props => props.visible ? 1 : 0};
  transform: ${props => props.visible ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)'};
  transition: all 0.3s ease;
  pointer-events: ${props => props.visible ? 'auto' : 'none'};
`;

const ViewOption = styled.button`
  width: 100%;
  padding: 12px 20px;
  background: none;
  border: none;
  color: ${props => props.active ? '#4CAF50' : 'rgba(255, 255, 255, 0.8)'};
  font-size: 0.9rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
  
  .checkmark {
    color: #4CAF50;
    opacity: ${props => props.active ? 1 : 0};
    transition: opacity 0.2s ease;
  }
`;

/**
 * ViewSelector component for switching between different view modes
 * 
 * @param {string} title - The title to display
 * @param {string} currentView - Currently selected view mode
 * @param {Array} options - Array of view options {value, label}
 * @param {function} onViewChange - Callback when view changes
 * @param {string} size - Size variant ('normal' | 'large')
 * @param {boolean} disabled - Whether the selector is disabled
 */
const ViewSelector = ({
  title = "View Mode",
  currentView,
  options = [
    { value: 'individual', label: 'Individual Paths' },
    { value: 'clustered', label: 'Clustered View' }
  ],
  onViewChange = () => {},
  size = 'normal',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleOptionClick = (option) => {
    setIsOpen(false);
    onViewChange(option.value);
  };

  const handleTriggerClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <Container ref={containerRef}>
      <TriggerButton
        onClick={handleTriggerClick}
        size={size}
        clickable={!disabled}
        disabled={disabled}
      >
        {title}
      </TriggerButton>
      
      <DropdownMenu visible={isOpen && !disabled}>
        {options.map((option) => (
          <ViewOption
            key={option.value}
            active={currentView === option.value}
            onClick={() => handleOptionClick(option)}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'inherit' }}>
              {option.icon && <span style={{ fontSize: '1.2em' }}>{option.icon}</span>}
              <span style={{ color: 'inherit' }}>{option.label}</span>
            </span>
            <span className="checkmark">✓</span>
          </ViewOption>
        ))}
      </DropdownMenu>
    </Container>
  );
};

export default ViewSelector;